import { useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { cn } from '../shared/utils/cn'
import { CompareSelection, CompareVariant, SensorTuple, MAX_COMPARE_SERIES } from '../contexts/AppContext'
import {
  DeviceSensorInfo,
  COMPARABLE_SENSOR_TYPES,
  SENSOR_SUB_TYPES,
  SensorSubType,
  getComparableTypeByProduct,
  DEVICE_LEVEL_SENSORS,
} from './SensorTypeSelector'
import {
  SubMetric,
  SUB_METRICS,
  SUB_METRIC_LABELS,
  SUB_METRIC_PILL_LABELS,
  makePortToken,
} from '../utils/portTokens'

// All possible ports in display order
const ALL_PORTS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'C3'] as const

// Short indicators shown in each mixed-mode cell so the user can tell what
// sensor type occupies a given port on a given device at a glance.
const TYPE_ABBREV: Record<string, string> = {
  temperature: 'Temp',
  humidity: 'Hum',
  windSpeed: 'Wind',
  solarRadiation: 'Solar',
  soilMoistureVH400: 'VH400',
  soilMoistureIrrometer: 'Irr',
  soilTemperature: 'SoilT',
  battery: 'Batt',
  portCPower: 'PwrC',
  pressure: 'Pres',
  rainfall: 'Rain',
  flowMeter: 'Flow',
}

interface SensorCompareGridProps {
  deviceSensorInfos: DeviceSensorInfo[]
  selectedSensors: CompareSelection[]
  selectedSensorType: string | null
  selectedSubType: SensorSubType | null
  compareVariant: CompareVariant
  showModeToggle: boolean
  onToggleSensor: (deviceId: string, sensorPort: string, sensorType: string) => void
  onToggleSensorMixed: (deviceId: string, sensorPort: string) => void
  onBulkToggleType: (tuples: SensorTuple[]) => void
  onSelectSensorType: (sensorType: string | null) => void
  onSelectSubType: (subType: SensorSubType | null) => void
  onSwitchSensorType: (newSensorType: string, deviceSensorMap: Map<string, string>) => void
  onSetVariant: (variant: CompareVariant) => void
  onClearAll: () => void
  className?: string
}

interface CellInfo {
  port: string
  sensorType: string | null
  hasSubTypes: boolean
  isSelected: boolean
  isDisabled: boolean
  // Sub-typed ports (rainfall/flow) in mixed mode render one checkbox per
  // metric instead of a single one, so the cell tracks which are picked.
  subTypeKey: 'rainfall' | 'flowMeter' | null
  selectedMetrics: SubMetric[]
}

// Get the comparable sensor type key for a product name
function getSensorTypeKey(productName: string): string | null {
  return getComparableTypeByProduct(productName)?.key ?? null
}

export function SensorCompareGrid({
  deviceSensorInfos,
  selectedSensors,
  selectedSensorType,
  selectedSubType,
  compareVariant,
  showModeToggle,
  onToggleSensor,
  onToggleSensorMixed,
  onBulkToggleType,
  onSelectSensorType,
  onSelectSubType,
  onSwitchSensorType,
  onSetVariant,
  onClearAll,
  className = '',
}: SensorCompareGridProps) {
  const isMixed = compareVariant === 'mixed'

  // Calculate available sensor types across all selected devices
  const availableSensorTypes = useMemo(() => {
    const typeCounts: Record<string, number> = {}

    for (const deviceInfo of deviceSensorInfos) {
      const deviceTypes = new Set<string>()
      for (const config of Object.values(deviceInfo.sensorConfigs)) {
        const sensorType = getSensorTypeKey(config.sensorProductName)
        if (sensorType) {
          deviceTypes.add(sensorType)
        }
      }
      // Count each type once per device
      deviceTypes.forEach(type => {
        typeCounts[type] = (typeCounts[type] || 0) + 1
      })
    }

    // Return types that exist in at least one device, sorted by count
    return COMPARABLE_SENSOR_TYPES
      .filter(t => typeCounts[t.key] > 0)
      .map(t => ({
        ...t,
        deviceCount: typeCounts[t.key] || 0,
      }))
      .sort((a, b) => b.deviceCount - a.deviceCount)
  }, [deviceSensorInfos])

  // Build the grid data
  // Count every selected sensor, including device-level picks (T0/Battery) that
  // aren't part of the A1..C3 grid cells.
  const totalSelected = selectedSensors.reduce((sum, s) => sum + s.sensorPorts.length, 0)
  // Mixed mode caps total series (readability + fan-out). At the limit, only
  // already-selected sensors stay toggleable so the user can swap.
  const atSeriesLimit = isMixed && totalSelected >= MAX_COMPARE_SERIES

  const { gridData } = useMemo(() => {
    const rows: { deviceId: string; deviceName: string; siteName: string; cells: CellInfo[] }[] = []

    for (const deviceInfo of deviceSensorInfos) {
      const deviceSelection = selectedSensors.find(s => s.deviceId === deviceInfo.deviceId)
      const cells: CellInfo[] = []

      for (const port of ALL_PORTS) {
        const config = deviceInfo.sensorConfigs[port]

        if (!config) {
          cells.push({
            port, sensorType: null, hasSubTypes: false, isSelected: false,
            isDisabled: true, subTypeKey: null, selectedMetrics: [],
          })
        } else {
          const typeConfig = getComparableTypeByProduct(config.sensorProductName)
          const sensorType = typeConfig?.key ?? null
          const hasSubTypes = !!typeConfig && 'hasSubTypes' in typeConfig && typeConfig.hasSubTypes === true
          const selectedTokens = deviceSelection?.sensorPorts ?? []

          // Mixed mode splits a rainfall/flow port into its three metrics, each
          // selected by its own "port~metric" token.
          const subTypeKey = isMixed && hasSubTypes && typeConfig && 'subTypeKey' in typeConfig
            ? typeConfig.subTypeKey
            : null
          const selectedMetrics = subTypeKey
            ? SUB_METRICS.filter(m => selectedTokens.includes(makePortToken(port, m)))
            : []
          const isSelected = subTypeKey
            ? selectedMetrics.length > 0
            : selectedTokens.includes(port)

          // Mixed: every comparable sensor is independently selectable. Sub-typed
          // cells stay enabled at the series limit — their individual metric
          // checkboxes enforce it, so already-picked metrics remain removable.
          // Single: only cells matching the chosen type are enabled.
          const isDisabled = isMixed
            ? sensorType === null || (!subTypeKey && atSeriesLimit && !isSelected)
            : selectedSensorType !== null && sensorType !== selectedSensorType

          cells.push({ port, sensorType, hasSubTypes, isSelected, isDisabled, subTypeKey, selectedMetrics })
        }
      }

      rows.push({
        deviceId: deviceInfo.deviceId,
        deviceName: deviceInfo.deviceName,
        siteName: deviceInfo.siteName || '',
        cells,
      })
    }

    return { gridData: rows }
  }, [deviceSensorInfos, selectedSensors, selectedSensorType, isMixed, atSeriesLimit])

  // Which mixed-mode "add all of type" pills are fully selected
  const isTypeFullySelected = (typeKey: string): boolean => {
    const tuples = tuplesForType(typeKey)
    if (tuples.length === 0) return false
    return tuples.every(t =>
      selectedSensors.find(s => s.deviceId === t.deviceId)?.sensorPorts.includes(t.sensorPort) ?? false
    )
  }

  // All (device, port) pairs of a given type across the selected devices
  function tuplesForType(typeKey: string): SensorTuple[] {
    const tuples: SensorTuple[] = []
    for (const deviceInfo of deviceSensorInfos) {
      for (const [port, config] of Object.entries(deviceInfo.sensorConfigs)) {
        if (getSensorTypeKey(config.sensorProductName) === typeKey) {
          tuples.push({ deviceId: deviceInfo.deviceId, sensorPort: port })
        }
      }
    }
    return tuples
  }

  // Same, but for one metric of a sub-typed type — the tuples carry
  // "port~metric" tokens so each metric bulk-toggles independently.
  function tuplesForTypeMetric(typeKey: string, metric: SubMetric): SensorTuple[] {
    return tuplesForType(typeKey).map(t => ({
      deviceId: t.deviceId,
      sensorPort: makePortToken(t.sensorPort, metric),
    }))
  }

  const isTypeMetricFullySelected = (typeKey: string, metric: SubMetric): boolean => {
    const tuples = tuplesForTypeMetric(typeKey, metric)
    if (tuples.length === 0) return false
    return tuples.every(t =>
      selectedSensors.find(s => s.deviceId === t.deviceId)?.sensorPorts.includes(t.sensorPort) ?? false
    )
  }

  // Sub-typed types present on the selected devices (mixed mode only) — each
  // gets a row of per-metric bulk pills.
  const subTypedSensorTypes = availableSensorTypes.filter(
    t => 'hasSubTypes' in t && t.hasSubTypes === true
  )

  // Device-level series (Temperature/Battery) apply to every selected device.
  const isPortSelected = (deviceId: string, port: string): boolean =>
    selectedSensors.find(s => s.deviceId === deviceId)?.sensorPorts.includes(port) ?? false
  const deviceLevelTuples = (port: string): SensorTuple[] =>
    deviceSensorInfos.map(d => ({ deviceId: d.deviceId, sensorPort: port }))
  const isDeviceLevelFullySelected = (port: string): boolean => {
    const tuples = deviceLevelTuples(port)
    return tuples.length > 0 && tuples.every(t => isPortSelected(t.deviceId, t.sensorPort))
  }

  // Device-level columns: all of them in mixed mode; in single mode only the
  // one currently selected type (its column is the whole grid).
  const visibleDeviceLevelSensors = isMixed
    ? DEVICE_LEVEL_SENSORS
    : DEVICE_LEVEL_SENSORS.filter(s => s.key === selectedSensorType)

  const minDevices = isMixed ? 1 : 2
  // Not enough devices for the current mode — we still render the header (so the
  // mode toggle stays reachable, e.g. to switch to single-device mixed mode) but
  // replace the pills/grid with a prompt.
  const needsMoreDevices = deviceSensorInfos.length < minDevices
  const showGrid = !needsMoreDevices && (isMixed || !!selectedSensorType)

  return (
    <div className={cn('bg-white border-b border-gray-200', className)}>
      {/* Header with sensor type filters */}
      <div className="px-4 py-3 bg-purple-50/50 border-b border-purple-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-purple-800">
              Select Sensors to Compare
            </span>
            {isMixed ? (
              <span
                className={cn(
                  'text-xs px-2 py-0.5 rounded-full',
                  atSeriesLimit ? 'text-amber-700 bg-amber-100' : 'text-purple-600 bg-purple-100'
                )}
              >
                {totalSelected} of {MAX_COMPARE_SERIES} series
              </span>
            ) : (
              totalSelected > 0 && (
                <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                  {totalSelected} selected
                </span>
              )
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Mode toggle: Single type vs Mixed types */}
            {showModeToggle && (
              <div
                className="inline-flex rounded-md border border-purple-300 overflow-hidden text-xs"
                title="Single type: one sensor type across devices. Mixed types: overlay different sensor types on one chart."
              >
                <button
                  onClick={() => onSetVariant('single')}
                  className={cn(
                    'px-2.5 py-1 font-medium transition-all',
                    !isMixed ? 'bg-purple-600 text-white' : 'bg-white text-purple-700 hover:bg-purple-50'
                  )}
                >
                  Single type
                </button>
                <button
                  onClick={() => onSetVariant('mixed')}
                  className={cn(
                    'px-2.5 py-1 font-medium transition-all border-l border-purple-300',
                    isMixed ? 'bg-purple-600 text-white' : 'bg-white text-purple-700 hover:bg-purple-50'
                  )}
                >
                  Mixed types
                </button>
              </div>
            )}

            {totalSelected > 0 && (
              <button
                onClick={onClearAll}
                className="text-xs text-purple-600 hover:text-purple-800 hover:underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Not enough devices for the current mode */}
        {needsMoreDevices && (
          <p className="text-sm text-purple-600">
            {isMixed
              ? 'Select at least 1 device to compare its sensors'
              : 'Select at least 2 devices to compare their sensors'}
          </p>
        )}

        {/* Sensor Type Filter Buttons */}
        {!needsMoreDevices && (
        <div className="flex flex-wrap gap-2">
          {availableSensorTypes.map(sensorType => {
            const sensorTypeConfig = COMPARABLE_SENSOR_TYPES.find(t => t.key === sensorType.key)
            const hasSubTypes = sensorTypeConfig && 'hasSubTypes' in sensorTypeConfig && sensorTypeConfig.hasSubTypes

            // Mixed mode: pills are bulk "add all of type" toggles. Sub-typed
            // sensors (rainfall/flow) get their own per-metric row below.
            if (isMixed) {
              if (hasSubTypes) return null
              const active = isTypeFullySelected(sensorType.key)
              // Blocked when at the series limit and this pill would only add.
              const blocked = atSeriesLimit && !active
              return (
                <button
                  key={sensorType.key}
                  disabled={blocked}
                  title={
                    blocked
                      ? `Limit of ${MAX_COMPARE_SERIES} series reached — remove one to add another`
                      : `Add/remove all ${sensorType.label} sensors`
                  }
                  onClick={() => onBulkToggleType(tuplesForType(sensorType.key))}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-full border transition-all',
                    blocked
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : active
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                  )}
                >
                  {sensorType.label}
                </button>
              )
            }

            // Single mode: pills select the one active sensor type
            const isActive = selectedSensorType === sensorType.key
            return (
              <button
                key={sensorType.key}
                onClick={() => {
                  if (isActive) {
                    // Clicking active type clears it
                    onSelectSensorType(null)
                    onSelectSubType(null)
                  } else {
                    // Smart sensor type switching: auto-select sensors for ALL devices in the comparison
                    const deviceSensorMap = new Map<string, string>()

                    for (const deviceInfo of deviceSensorInfos) {
                      for (const [port, config] of Object.entries(deviceInfo.sensorConfigs)) {
                        const configSensorType = getSensorTypeKey(config.sensorProductName)
                        if (configSensorType === sensorType.key) {
                          deviceSensorMap.set(deviceInfo.deviceId, port)
                          break // Only select one sensor per device
                        }
                      }
                    }

                    // Use atomic switch to avoid race condition
                    onSwitchSensorType(sensorType.key, deviceSensorMap)

                    // If this sensor type has sub-types, select the first one by default
                    if (hasSubTypes && sensorTypeConfig && 'subTypeKey' in sensorTypeConfig) {
                      const subTypes = SENSOR_SUB_TYPES[sensorTypeConfig.subTypeKey]
                      if (subTypes && subTypes.length > 0) {
                        onSelectSubType(subTypes[0].key)
                      }
                    } else {
                      onSelectSubType(null)
                    }
                  }
                }}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full border transition-all',
                  isActive
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                )}
              >
                {sensorType.label}
              </button>
            )
          })}

          {/* Device-level pills: Temperature (T0) + Battery apply to every
              selected device and aren't in the port grid. In single mode they
              behave like the type pills above; in mixed mode they bulk-toggle.
              Skipped in single mode when a physical port already provides the
              same type, so the pill never appears twice. */}
          {DEVICE_LEVEL_SENSORS
            .filter(s => isMixed || !availableSensorTypes.some(t => t.key === s.key))
            .map(s => {
              if (!isMixed) {
                const isActive = selectedSensorType === s.key
                return (
                  <button
                    key={s.port}
                    title={`Compare ${s.label} across devices`}
                    onClick={() => {
                      if (isActive) {
                        onSelectSensorType(null)
                        onSelectSubType(null)
                      } else {
                        // Device-level series exist on every device — select all.
                        onSwitchSensorType(
                          s.key,
                          new Map(deviceSensorInfos.map(d => [d.deviceId, s.port]))
                        )
                        onSelectSubType(null)
                      }
                    }}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-full border transition-all',
                      isActive
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                    )}
                  >
                    {s.label}
                  </button>
                )
              }

              const active = isDeviceLevelFullySelected(s.port)
              const blocked = atSeriesLimit && !active
              return (
                <button
                  key={s.port}
                  disabled={blocked}
                  title={blocked
                    ? `Limit of ${MAX_COMPARE_SERIES} series reached — remove one to add another`
                    : `Add/remove ${s.label} for all devices`}
                  onClick={() => onBulkToggleType(deviceLevelTuples(s.port))}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-full border transition-all',
                    blocked
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : active
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                  )}
                >
                  {s.label}
                </button>
              )
            })}
        </div>
        )}

        {/* Per-metric bulk pills (mixed mode) for Rainfall / Flow Meter. Each
            metric is its own chart series, so each pill toggles that metric
            across every device that has the sensor. */}
        {!needsMoreDevices && isMixed && subTypedSensorTypes.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {subTypedSensorTypes.map(sensorType => (
              <div key={sensorType.key} className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-600 w-20 shrink-0">
                  {sensorType.label}:
                </span>
                {SUB_METRICS.map(metric => {
                  const active = isTypeMetricFullySelected(sensorType.key, metric)
                  const blocked = atSeriesLimit && !active
                  return (
                    <button
                      key={metric}
                      disabled={blocked}
                      title={blocked
                        ? `Limit of ${MAX_COMPARE_SERIES} series reached — remove one to add another`
                        : `Add/remove ${sensorType.label} ${SUB_METRIC_PILL_LABELS[metric]} for all devices`}
                      onClick={() => onBulkToggleType(tuplesForTypeMetric(sensorType.key, metric))}
                      className={cn(
                        'px-3 py-1 text-xs font-medium rounded-full border transition-all',
                        blocked
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : active
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                      )}
                    >
                      {SUB_METRIC_PILL_LABELS[metric]}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        )}

        {/* Sub-type Selection (single mode only, for Rainfall and Flow Meter) */}
        {!needsMoreDevices && !isMixed && selectedSensorType && (() => {
          const currentTypeConfig = COMPARABLE_SENSOR_TYPES.find(t => t.key === selectedSensorType)
          if (!currentTypeConfig || !('hasSubTypes' in currentTypeConfig) || !currentTypeConfig.hasSubTypes) {
            return null
          }
          const subTypeKey = currentTypeConfig.subTypeKey as keyof typeof SENSOR_SUB_TYPES
          const subTypes = SENSOR_SUB_TYPES[subTypeKey]
          if (!subTypes) return null

          return (
            <div className="mt-3 pt-3 border-t border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-purple-600">
                  Select metric to compare:
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {subTypes.map(subType => {
                  const isSubActive = selectedSubType === subType.key
                  return (
                    <button
                      key={subType.key}
                      onClick={() => onSelectSubType(subType.key)}
                      className={cn(
                        'px-3 py-1 text-xs font-medium rounded border transition-all',
                        isSubActive
                          ? 'bg-indigo-500 text-white border-indigo-500'
                          : 'bg-white text-indigo-700 border-indigo-300 hover:border-indigo-400 hover:bg-indigo-50'
                      )}
                    >
                      {subType.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })()}

        {!needsMoreDevices && isMixed && (
          <p className="mt-2 text-xs text-purple-500">
            Pick any sensors across the grid — different types are overlaid and auto-scaled so you can compare trends.
          </p>
        )}
      </div>

      {/* Grid */}
      {showGrid && (
        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Device
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Site
                </th>
                {ALL_PORTS.map(port => (
                  <th
                    key={port}
                    className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10"
                  >
                    {port}
                  </th>
                ))}
                {/* Device-level columns (Temperature/Battery) */}
                {visibleDeviceLevelSensors.map(s => (
                  <th
                    key={s.port}
                    className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12 border-l border-gray-200"
                  >
                    {TYPE_ABBREV[s.key] ?? s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {gridData.map(row => (
                <tr key={row.deviceId} className="hover:bg-gray-50/50">
                  <td className="px-3 py-1.5 text-sm font-medium text-gray-900 whitespace-nowrap">
                    {row.deviceName}
                  </td>
                  <td className="px-3 py-1.5 text-sm text-gray-600 whitespace-nowrap max-w-[150px] truncate" title={row.siteName}>
                    {row.siteName || '—'}
                  </td>
                  {row.cells.map(cell => (
                    <td key={cell.port} className="px-1 py-1 text-center align-top">
                      {cell.sensorType === null || cell.isDisabled ? (
                        // Empty or disabled cell
                        <div
                          className="w-6 h-6 mx-auto rounded border border-gray-200 bg-gray-50"
                          title={cell.sensorType && atSeriesLimit && !cell.isSelected
                            ? `Limit of ${MAX_COMPARE_SERIES} series reached — remove one to add another`
                            : undefined}
                        />
                      ) : cell.subTypeKey ? (
                        // Rainfall / flow meter in mixed mode: one checkbox per
                        // metric, since each is its own chart series.
                        <div className="flex flex-col items-center gap-0.5">
                          <div className="flex items-start justify-center gap-0.5">
                            {SUB_METRICS.map(metric => {
                              const token = makePortToken(cell.port, metric)
                              const selected = cell.selectedMetrics.includes(metric)
                              const blocked = atSeriesLimit && !selected
                              return (
                                <div key={metric} className="flex flex-col items-center gap-0.5">
                                  <button
                                    onClick={() => onToggleSensorMixed(row.deviceId, token)}
                                    disabled={blocked}
                                    title={blocked
                                      ? `Limit of ${MAX_COMPARE_SERIES} series reached — remove one to add another`
                                      : `${COMPARABLE_SENSOR_TYPES.find(t => t.key === cell.sensorType)?.label} — ${SUB_METRIC_PILL_LABELS[metric]}`}
                                    className={cn(
                                      'w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-all',
                                      selected
                                        ? 'border-blue-500 bg-blue-500 text-white'
                                        : blocked
                                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                          : 'border-gray-300 bg-white hover:border-blue-400'
                                    )}
                                  >
                                    {selected && <FontAwesomeIcon icon={faCheck} className="text-[7px]" />}
                                  </button>
                                  <span className="text-[8px] leading-none text-gray-400">
                                    {SUB_METRIC_LABELS[metric]}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                          <span className="text-[9px] leading-none text-gray-400">
                            {TYPE_ABBREV[cell.sensorType] ?? cell.sensorType}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-0.5">
                          <button
                            onClick={() =>
                              isMixed
                                ? onToggleSensorMixed(row.deviceId, cell.port)
                                : cell.sensorType && onToggleSensor(row.deviceId, cell.port, cell.sensorType)
                            }
                            title={COMPARABLE_SENSOR_TYPES.find(t => t.key === cell.sensorType)?.label}
                            className={cn(
                              'w-6 h-6 rounded border-2 flex items-center justify-center transition-all mx-auto',
                              cell.isSelected
                                ? 'border-blue-500 bg-blue-500 text-white'
                                : 'border-gray-300 bg-white hover:border-blue-400'
                            )}
                          >
                            {cell.isSelected && (
                              <FontAwesomeIcon icon={faCheck} className="text-[10px]" />
                            )}
                          </button>
                          {/* Type indicator (mixed mode only) */}
                          {isMixed && cell.sensorType && (
                            <span className="text-[9px] leading-none text-gray-400">
                              {TYPE_ABBREV[cell.sensorType] ?? cell.sensorType}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  ))}
                  {/* Device-level cells: Temperature (T0) + Battery, always
                      selectable since every device reports them. */}
                  {visibleDeviceLevelSensors.map(s => {
                    const selected = isPortSelected(row.deviceId, s.port)
                    const blocked = isMixed && atSeriesLimit && !selected
                    return (
                      <td key={s.port} className="px-1 py-1 text-center align-top border-l border-gray-100">
                        <div className="flex flex-col items-center gap-0.5">
                          <button
                            onClick={() =>
                              isMixed
                                ? onToggleSensorMixed(row.deviceId, s.port)
                                : onToggleSensor(row.deviceId, s.port, s.key)
                            }
                            disabled={blocked}
                            title={blocked
                              ? `Limit of ${MAX_COMPARE_SERIES} series reached — remove one to add another`
                              : s.label}
                            className={cn(
                              'w-6 h-6 rounded border-2 flex items-center justify-center transition-all mx-auto',
                              selected
                                ? 'border-blue-500 bg-blue-500 text-white'
                                : blocked
                                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                  : 'border-gray-300 bg-white hover:border-blue-400'
                            )}
                          >
                            {selected && <FontAwesomeIcon icon={faCheck} className="text-[10px]" />}
                          </button>
                          <span className="text-[9px] leading-none text-gray-400">
                            {TYPE_ABBREV[s.key] ?? s.label}
                          </span>
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Help text when no sensor type selected (single mode) */}
      {!needsMoreDevices && !isMixed && !selectedSensorType && (
        <div className="px-4 py-4 text-center text-sm text-gray-500">
          Select a sensor type above to start comparing
        </div>
      )}
    </div>
  )
}
